import { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import { Container, Typography, Box } from '@mui/material';
import gsap from "gsap";

function TrasparenzaIA() {
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (pageRef.current) {
      gsap.fromTo(
        pageRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power2.out" }
      );
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Trasparenza sull'uso dell'Intelligenza Artificiale | CAMILLACINODESIGN&DEV</title>
        <meta name="description" content="Dichiarazione di trasparenza sull'utilizzo dell'intelligenza artificiale nel sito camillacinodesign.dev, ai sensi del Regolamento UE 2024/1689 (AI Act)." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <Box
        ref={pageRef}
        sx={{
          minHeight: '100vh',
          pt: { xs: 14, md: 18 },
          pb: 10,
          backgroundColor: '#101010',
          color: '#fff',
        }}
      >
        <Container maxWidth="xl">
          <Typography
            variant="h2"
            sx={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.8rem' },
              mb: 2,
              background: 'linear-gradient(135deg, #048091, #7E82F7)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Trasparenza sull'uso dell'Intelligenza Artificiale
          </Typography>

          <Typography
            variant="body2"
            sx={{
              fontFamily: "'Space Grotesk', sans-serif",
              color: 'rgba(255,255,255,0.5)',
              mb: 6,
            }}
          >
            Ultimo aggiornamento: 20 agosto 2026
          </Typography>

          <Section title="1. Dichiarazione generale">
            <p>
              CAMILLACINODESIGN&DEV dichiara che alcuni contenuti presenti su questo sito sono stati generati
              o elaborati con l'ausilio di strumenti di intelligenza artificiale, in conformità all'<strong>articolo 50
              del Regolamento UE 2024/1689</strong> (AI Act).
            </p>
            <p>
              La presente pagina ha lo scopo di informare l'utente in modo trasparente su quali contenuti
              siano stati prodotti con l'IA, con quali strumenti e per quali finalità.
            </p>
          </Section>

          <Section title="2. Contenuti generati con IA">
            <p>
              Di seguito l'elenco dei contenuti presenti sul sito che sono stati generati o elaborati
              tramite strumenti di intelligenza artificiale:
            </p>

            <SubSection title="2.1 Video decorativi">
              <p>Tutti i video presenti nel sito sono stati generati tramite intelligenza artificiale e sono utilizzati come sfondi decorativi:</p>
              <ul>
                <li><strong>video1.mp4</strong> — Video decorativo generato con IA.</li>
                <li><strong>video2.mp4</strong> — Video decorativo utilizzato come sfondo nelle pagine: Homepage, Contatti, Lavori, Blog, Servizi, Partners, Prezzi.</li>
                <li><strong>video3.mp4</strong> — Video decorativo utilizzato come sfondo nella pagina Chi sono.</li>
                <li><strong>video5.mp4</strong> — Video decorativo generato con IA.</li>
              </ul>
              <p>
                Tutti i video hanno funzione puramente estetica e decorativa. Non rappresentano riprese reali.
              </p>
            </SubSection>

            <SubSection title="2.2 Sfondi e immagini decorative">
              <p>Le seguenti immagini sono state generate tramite intelligenza artificiale:</p>
              <ul>
                <li><strong>sfondo1.png</strong> — Sfondo decorativo utilizzato in diverse sezioni del sito.</li>
                <li><strong>sfondo2.png</strong> — Sfondo decorativo utilizzato in diverse sezioni del sito.</li>
                <li><strong>sfondo3.png</strong> — Sfondo decorativo utilizzato in diverse sezioni del sito.</li>
                <li><strong>blog.png</strong> — Immagine di copertina utilizzata per gli articoli del Blog.</li>
                <li><strong>contatti.png</strong> — Illustrazione decorativa nella pagina Contatti.</li>
                <li><strong>sfera.png</strong> — Sfera 3D decorativa nella pagina Partners.</li>
              </ul>
            </SubSection>

            <SubSection title="2.3 Icone delle tecnologie">
              <p>Le icone delle tecnologie mostrate nella pagina Servizi sono state generate con IA:</p>
              <ul>
                <li><strong>figma.webp</strong> — Icona Figma</li>
                <li><strong>gsap.webp</strong> — Icona GSAP</li>
                <li><strong>html.webp</strong> — Icona HTML</li>
                <li><strong>python.webp</strong> — Icona Python</li>
                <li><strong>react.webp</strong> — Icona React</li>
                <li><strong>scss.webp</strong> — Icona SCSS</li>
                <li><strong>threejs.webp</strong> — Icona Three.js</li>
                <li><strong>ts.webp</strong> — Icona TypeScript</li>
              </ul>
            </SubSection>

            <SubSection title="2.4 Assistente virtuale (Chatbot)">
              <p>
                Il sito integra un assistente virtuale basato su intelligenza artificiale. L'utente
                viene informato che sta interagendo con un sistema automatizzato e non con una persona reale.
              </p>
            </SubSection>

            <SubSection title="2.5 Testi e contenuti editoriali">
              <p>
                Alcuni testi presenti nel sito (inclusi articoli del blog) possono essere stati redatti
                con il supporto di strumenti di IA. Tutti i contenuti vengono sempre revisionati,
                verificati e approvati dalla titolare prima della pubblicazione.
              </p>
            </SubSection>

            <SubSection title="2.6 Codice sorgente">
              <p>
                Parti del codice sorgente del sito sono state sviluppate con il supporto di strumenti
                di intelligenza artificiale (assistenti di programmazione). Tutto il codice viene revisionato
                e validato dalla sviluppatrice.
              </p>
            </SubSection>
          </Section>

          <Section title="3. Contenuti NON generati con IA">
            <ul>
              <li>Fotografia personale nella sezione "Chi sono" (<strong>me.png</strong> — Camilla Cino).</li>
              <li>Screenshot e immagini dei lavori e progetti realizzati (cartella <code>/lavori/</code> e <code>/lavori-selezionati/</code>).</li>
              <li>Mockup dei progetti (cartella <code>/mockup/</code>).</li>
              <li>Loghi dei partner (<strong>cell_gig.svg</strong>, <strong>ru_grafica.png</strong>, <strong>stech_logo.png</strong>, <strong>tenkan_communication.png</strong>, <strong>voltarenstudio_logo.png</strong>).</li>
              <li>Logo CAMILLACINODESIGN&DEV e relative varianti.</li>
              <li>Preventivi, comunicazioni dirette e documenti contrattuali.</li>
            </ul>
          </Section>

          <Section title="4. Strumenti di IA utilizzati">
            <p>
              Gli strumenti di intelligenza artificiale utilizzati per la creazione dei contenuti includono:
            </p>
            <ul>
              <li><strong>Generazione immagini:</strong> strumenti di text-to-image per immagini decorative.</li>
              <li><strong>Assistenza testuale:</strong> modelli linguistici (LLM) per supporto alla redazione di testi.</li>
              <li><strong>Chatbot:</strong> sistema conversazionale per l'assistenza automatizzata ai visitatori.</li>
            </ul>
          </Section>

          <Section title="5. Finalità dell'utilizzo dell'IA">
            <ul>
              <li>Migliorare l'esperienza visiva e di navigazione del sito.</li>
              <li>Fornire assistenza rapida e automatizzata ai visitatori.</li>
              <li>Supportare la creazione di contenuti informativi per il blog.</li>
              <li>Ottimizzare i tempi di produzione senza compromettere la qualità.</li>
            </ul>
            <p>
              L'intelligenza artificiale <strong>non</strong> viene utilizzata per prendere decisioni automatizzate
              che producano effetti giuridici o significativi sull'utente.
            </p>
          </Section>

          <Section title="6. Accessibilità dei contenuti IA">
            <p>
              Per garantire l'accessibilità ai sensi della Direttiva (UE) 2019/882:
            </p>
            <ul>
              <li>Le immagini IA recano <code>alt</code> descrittivo con indicazione "generata con IA".</li>
              <li>Il chatbot è progettato per essere navigabile da tastiera e compatibile con screen reader.</li>
              <li>I video decorativi eventualmente generati con IA riportano <code>aria-label</code> appropriato.</li>
            </ul>
          </Section>

          <Section title="7. Riferimenti normativi">
            <ul>
              <li>Regolamento UE 2024/1689 (AI Act), in particolare l'articolo 50 sugli obblighi di trasparenza.</li>
              <li>Regolamento UE 2016/679 (GDPR).</li>
              <li>Direttiva (UE) 2019/882 (European Accessibility Act).</li>
            </ul>
          </Section>

          <Section title="8. Aggiornamenti">
            <p>
              Questa pagina viene aggiornata ogni volta che nuovo contenuto generato con IA viene aggiunto al sito.
              Si consiglia di consultarla periodicamente per rimanere informati.
            </p>
          </Section>

          <Section title="9. Contatti">
            <p>
              Per domande o chiarimenti sull'utilizzo dell'intelligenza artificiale nel sito:
            </p>
            <Box
              sx={{
                backgroundColor: 'rgba(4, 128, 145, 0.1)',
                border: '1px solid rgba(4, 128, 145, 0.3)',
                borderRadius: 2,
                p: 3,
                mt: 2,
              }}
            >
              <p style={{ margin: 0 }}>
                <strong>Camilla Cino - CAMILLACINODESIGN&DEV</strong><br />
                Via Per S. Felice 3, Camposanto (MO) 41031<br />
                P.IVA: 04601200167<br />
                Email: <a href="mailto:info@camillacinodesigndev.it" style={{ color: '#048091' }}>info@camillacinodesigndev.it</a><br />
                Sito: <a href="https://www.camillacinodesign.dev" style={{ color: '#048091' }}>www.camillacinodesign.dev</a>
              </p>
            </Box>
          </Section>

        </Container>
      </Box>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 5 }}>
      <Typography
        variant="h5"
        sx={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: 600,
          fontSize: { xs: '1.2rem', md: '1.4rem' },
          mb: 2,
          color: '#fff',
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: '0.95rem',
          lineHeight: 1.8,
          color: 'rgba(255,255,255,0.8)',
          '& p': { mb: 2, mt: 0 },
          '& ul': { pl: 3, mb: 2 },
          '& li': { mb: 1 },
          '& code': {
            backgroundColor: 'rgba(126, 130, 247, 0.15)',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '0.85rem',
          },
          '& a': {
            color: '#048091',
            textDecoration: 'underline',
            '&:hover': { color: '#7E82F7' },
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="h6"
        sx={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: 500,
          fontSize: '1rem',
          mb: 1,
          color: 'rgba(255,255,255,0.9)',
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export default TrasparenzaIA;
